/**
 * Deterministic weather state machine (owner: env-builder).
 *
 * Pure logic: no Phaser import so it is unit-testable in Node. `WorldScene` ticks it on the fixed
 * 1/60 step, forwards every emission to `bus` as `weather:change { state, t }` and paints the
 * full-screen lighting overlay from `look()`.
 *
 * Canonical order (story): sultry → wind → drizzle → rain → interior-rain.
 */
import type { WeatherState } from '@/core/types';

export const WEATHER_ORDER: readonly WeatherState[] = ['sultry', 'wind', 'drizzle', 'rain', 'interior-rain'];

export function isWeatherState(x: unknown): x is WeatherState {
  return typeof x === 'string' && (WEATHER_ORDER as readonly string[]).includes(x);
}

export function weatherIndex(state: WeatherState): number {
  return WEATHER_ORDER.indexOf(state);
}

/** Visual + intensity description of a settled weather state. Colours are 0xRRGGBB. */
export interface WeatherLook {
  /** Multiply overlay (key light): warm haze before the rain, cooler diffuse light during. */
  tint: number;
  tintAlpha: number;
  /** Screen-blended warm haze / lantern glow layer. */
  haze: number;
  hazeAlpha: number;
  /** Suggested intensities (0..1) for effects/audio that want a single scalar. */
  rain: number;
  wind: number;
}

/** Palette anchors from ART_DIRECTION.md: sand #e2c48c, lantern #ffb347, storm sky #3a3f57. */
export const WEATHER_LOOKS: Record<WeatherState, WeatherLook> = {
  sultry: { tint: 0xffe8c4, tintAlpha: 0.32, haze: 0xffb347, hazeAlpha: 0.07, rain: 0, wind: 0.05 },
  wind: { tint: 0xf2e6d2, tintAlpha: 0.34, haze: 0xffc070, hazeAlpha: 0.04, rain: 0, wind: 0.6 },
  drizzle: { tint: 0xb9c6d8, tintAlpha: 0.44, haze: 0x8fa3c0, hazeAlpha: 0.02, rain: 0.35, wind: 0.5 },
  rain: { tint: 0x8794b4, tintAlpha: 0.58, haze: 0x3a3f57, hazeAlpha: 0.04, rain: 1, wind: 0.7 },
  'interior-rain': { tint: 0xb59a7a, tintAlpha: 0.46, haze: 0xffb347, hazeAlpha: 0.1, rain: 0.8, wind: 0.2 },
};

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(lerp(ar, br, t)), g = Math.round(lerp(ag, bg, t)), bl = Math.round(lerp(ab, bb, t));
  return (r << 16) | (g << 8) | bl;
}

export function lerpLook(a: WeatherLook, b: WeatherLook, t: number): WeatherLook {
  return {
    tint: lerpColor(a.tint, b.tint, t),
    tintAlpha: lerp(a.tintAlpha, b.tintAlpha, t),
    haze: lerpColor(a.haze, b.haze, t),
    hazeAlpha: lerp(a.hazeAlpha, b.hazeAlpha, t),
    rain: lerp(a.rain, b.rain, t),
    wind: lerp(a.wind, b.wind, t),
  };
}

/** Smoothstep ease used for the visual blend (progress `t` on the bus stays linear). */
export function easeInOut(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

export interface WeatherEmission { state: WeatherState; t: number }

/** Float tolerance for "transition finished" (fixed-step sums of 1000/60 drift below the target). */
const SETTLE_EPSILON_MS = 1e-6;

export class WeatherDirector {
  private target: WeatherState;
  private fromState: WeatherState;
  private fromLook: WeatherLook;
  private elapsed = 0;
  private duration = 0;
  private active = false;
  private emitAcc = 0;

  constructor(initial: WeatherState = 'sultry', private readonly emitIntervalMs = 100) {
    this.target = initial;
    this.fromState = initial;
    this.fromLook = WEATHER_LOOKS[initial];
  }

  /** The state being transitioned to (or the settled state). */
  get state(): WeatherState { return this.target; }
  get previous(): WeatherState { return this.fromState; }
  get transitioning(): boolean { return this.active; }
  /** Linear 0..1 progress of the running transition (1 when settled). */
  get progress(): number {
    if (!this.active) return 1;
    return this.duration <= 0 ? 1 : Math.min(1, this.elapsed / this.duration);
  }

  /**
   * Starts a timed transition. Returns the emission the caller should broadcast (t=0 for a timed
   * transition, t=1 for an instant one) or null when nothing changed.
   */
  set(state: WeatherState, transitionMs = 0): WeatherEmission | null {
    if (state === this.target && !this.active) return null;
    if (state === this.target && this.active) return null; // already heading there
    const currentLook = this.look();
    this.fromState = this.active ? this.target : this.target;
    this.fromLook = currentLook;
    this.target = state;
    this.elapsed = 0;
    this.emitAcc = 0;
    if (transitionMs <= 0) {
      this.active = false;
      this.duration = 0;
      this.fromState = state;
      this.fromLook = WEATHER_LOOKS[state];
      return { state, t: 1 };
    }
    this.active = true;
    this.duration = transitionMs;
    return { state, t: 0 };
  }

  /** Advances the transition. Returns at most one emission (throttled progress, or the final t=1). */
  tick(dtMs: number): WeatherEmission | null {
    if (!this.active) return null;
    this.elapsed += dtMs;
    // 60 × (1000/60) accumulates to 999.999…; treat anything within a nanosecond as settled.
    if (this.elapsed + SETTLE_EPSILON_MS >= this.duration) {
      this.active = false;
      this.elapsed = this.duration;
      this.fromState = this.target;
      this.fromLook = WEATHER_LOOKS[this.target];
      return { state: this.target, t: 1 };
    }
    this.emitAcc += dtMs;
    if (this.emitAcc >= this.emitIntervalMs) {
      this.emitAcc -= this.emitIntervalMs;
      return { state: this.target, t: this.progress };
    }
    return null;
  }

  /** Current blended look (eased). */
  look(): WeatherLook {
    if (!this.active) return WEATHER_LOOKS[this.target];
    return lerpLook(this.fromLook, WEATHER_LOOKS[this.target], easeInOut(this.progress));
  }
}
